/**
 * PortalLoyaltyPage — patient's rewards balances across every pharmacy
 * they've shopped at. Requires a phone number on the patient profile.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, TrendingUp, Trophy, Info, QrCode } from 'lucide-react';
import { usePortalDestination } from '../PortalDestinationContext';

const TIER_STYLE = {
  Bronze:   { grad: 'from-amber-500 to-amber-700',   icon: '🥉' },
  Silver:   { grad: 'from-slate-400 to-slate-600',   icon: '🥈' },
  Gold:     { grad: 'from-yellow-400 to-yellow-600', icon: '🥇' },
  Platinum: { grad: 'from-indigo-500 to-indigo-700', icon: '💎' },
};

export default function PortalLoyaltyPage() {
  const { api: portalApi, href } = usePortalDestination();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [qrUrls, setQrUrls] = useState({});     // loyalty_id → blob URL
  const [qrOpen, setQrOpen] = useState(null);    // loyalty_id of the enlarged QR

  useEffect(() => {
    portalApi.get('/loyalty')
      .then((r) => setData(r.data))
      .catch(() => setError('Could not load your rewards balance.'));
  }, [portalApi]);

  // Fetch QR bytes for each card once the accounts arrive.
  useEffect(() => {
    if (!data?.accounts?.length) return;
    let cancelled = false;
    const urls = {};
    (async () => {
      for (const a of data.accounts) {
        if (!a.member_code || !a.loyalty_id) continue;
        try {
          const res = await portalApi.get(`/loyalty/${a.loyalty_id}/qr.png`, { responseType: 'blob' });
          if (!cancelled) urls[a.loyalty_id] = URL.createObjectURL(res.data);
        } catch { /* ignore, fallback rendering */ }
      }
      if (!cancelled) setQrUrls({ ...urls });
    })();
    return () => {
      cancelled = true;
      Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [data, portalApi]);

  if (error) return <div className="text-sm text-red-600" data-testid="portal-loyalty-error">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading rewards…</div>;

  const accounts = data.accounts || [];

  return (
    <div className="space-y-5" data-testid="portal-loyalty-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-emerald-600" /> Rewards
        </h1>
        <p className="text-sm text-slate-600">
          Every purchase earns points. Save them up for discounts on future orders.
        </p>
      </div>

      {accounts.length === 0 && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-3" data-testid="portal-loyalty-empty">
          <Info className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-emerald-800">You haven&apos;t earned any points yet.</div>
            <p className="text-sm text-slate-700 mt-1">
              {data.message || 'Make sure your phone number is up to date, then any purchase you make will automatically start earning points.'}
            </p>
            <Link to={href('edit-profile')} data-testid="portal-rewards-profile-link" className="inline-block mt-3 text-sm font-medium text-emerald-700 hover:underline">
              Update my profile →
            </Link>
          </div>
        </div>
      )}

      {accounts.map((a) => {
        const style = TIER_STYLE[a.tier] || TIER_STYLE.Bronze;
        // Next tier: sort tiers ascending by threshold, pick the first one above lifetime.
        const sorted = [...(a.tiers || [])].sort((x, y) => (x.min_lifetime_points || 0) - (y.min_lifetime_points || 0));
        const next = sorted.find((t) => (t.min_lifetime_points || 0) > (a.lifetime_points || 0));
        const currentTier = sorted.filter((t) => (t.min_lifetime_points || 0) <= (a.lifetime_points || 0)).slice(-1)[0]
          || sorted[0] || { min_lifetime_points: 0 };
        const range = next ? Math.max(1, (next.min_lifetime_points || 0) - (currentTier.min_lifetime_points || 0)) : 1;
        const progressed = Math.max(0, (a.lifetime_points || 0) - (currentTier.min_lifetime_points || 0));
        const pctToNext = Math.min(100, Math.round((progressed / range) * 100));

        return (
          <div key={a.loyalty_id} className="rounded-3xl overflow-hidden shadow-md border border-emerald-100 bg-white" data-testid={`portal-loyalty-card-${a.loyalty_id}`}>
            <div className={`p-5 text-white bg-gradient-to-br ${style.grad}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm opacity-80">{a.tenant_name}</div>
                  <div className="text-xl font-bold tracking-tight">{a.program_name}</div>
                </div>
                <div className="text-4xl">{style.icon}</div>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="text-xs opacity-80 uppercase tracking-wider">Balance</div>
                  <div className="text-4xl font-extrabold tabular-nums">{(a.balance || 0).toLocaleString()}</div>
                  <div className="text-xs opacity-90">points ≈ {((a.balance || 0) / (a.redeem_ratio || 100)).toFixed(2)} off</div>
                </div>
                <div className="text-right">
                  <div className="text-xs opacity-80 uppercase tracking-wider">Tier</div>
                  <div className="text-2xl font-bold flex items-center gap-1"><Trophy className="w-5 h-5" /> {a.tier}</div>
                </div>
              </div>

              {next && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs opacity-90 mb-1">
                    <span>{a.tier} → {next.name}</span>
                    <span>{(a.lifetime_points || 0).toLocaleString()} / {(next.min_lifetime_points || 0).toLocaleString()} lifetime</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/25 overflow-hidden">
                    <div className="h-full bg-white/95 transition-all" style={{ width: `${pctToNext}%` }} />
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 space-y-3">
              {a.balance >= (a.min_redeem_points || 0) && a.redeem_ratio > 0 && (
                <Link to={href('shop')} className="block w-full text-center rounded-xl bg-emerald-600 text-white font-semibold text-sm py-2.5 hover:bg-emerald-700 transition"
                  data-testid={`portal-loyalty-redeem-link-${a.loyalty_id}`}>
                  Redeem for a discount in Shop →
                </Link>
              )}
              {/* QR card row — "show this to the cashier" */}
              {a.member_code && (
                <button
                  type="button"
                  className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 p-3 transition"
                  onClick={() => setQrOpen(a.loyalty_id)}
                  data-testid={`portal-loyalty-qr-btn-${a.loyalty_id}`}
                >
                  <div className="w-14 h-14 rounded-lg bg-white grid place-items-center border border-slate-200 shrink-0">
                    {qrUrls[a.loyalty_id] ? (
                      <img src={qrUrls[a.loyalty_id]} alt="Card QR" className="w-12 h-12" />
                    ) : (
                      <QrCode className="w-6 h-6 text-slate-300" />
                    )}
                  </div>
                  <div className="min-w-0 text-left">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Your Card</div>
                    <div className="font-mono font-semibold text-slate-800 tracking-wider">{a.member_code}</div>
                    <div className="text-[11px] text-slate-500">Tap to enlarge · show at checkout to earn or redeem</div>
                  </div>
                </button>
              )}

              <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                <span>Earn <b>{a.earn_rate}</b> pt / unit spent</span>
                <span>· Redeem <b>{a.redeem_ratio}</b> pts for 1 unit off</span>
                <span>· Min redemption <b>{a.min_redeem_points}</b> pts</span>
              </div>

              <div className="text-sm font-semibold text-slate-800 mt-3 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" /> Recent activity
              </div>
              <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto rounded-lg border border-slate-100">
                {(a.transactions || []).length === 0 && (
                  <div className="text-xs text-slate-500 px-3 py-4 text-center">No activity yet.</div>
                )}
                {(a.transactions || []).map((t) => (
                  <div key={t.txn_id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="text-slate-700 truncate">{t.reason || t.type}</div>
                      <div className="text-[11px] text-slate-400">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                    </div>
                    <div className={`shrink-0 tabular-nums font-semibold ${t.points > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {t.points > 0 ? `+${t.points}` : t.points}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {/* Fullscreen QR overlay — tap-anywhere to close */}
      {qrOpen && qrUrls[qrOpen] && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/85 backdrop-blur-sm grid place-items-center p-6"
          onClick={() => setQrOpen(null)}
          data-testid="portal-loyalty-qr-modal"
        >
          <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-xs w-full text-center" onClick={(e) => e.stopPropagation()}>
            <img src={qrUrls[qrOpen]} alt="Loyalty QR" className="w-64 h-64 mx-auto" />
            <div className="mt-3 font-mono text-lg font-bold tracking-widest text-emerald-700">
              {(data.accounts.find((a) => a.loyalty_id === qrOpen) || {}).member_code}
            </div>
            <p className="mt-2 text-xs text-slate-500">Show this to the cashier at checkout</p>
            <button
              type="button"
              className="mt-4 px-4 py-2 rounded-full bg-slate-900 text-white text-sm"
              onClick={() => setQrOpen(null)}
              data-testid="portal-loyalty-qr-close"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
